// src/components/KnowledgeDB/index.js
import React, { useState, useEffect } from "react";
import {
  Database,
  Share2,
  Trash2,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Upload,
  Info,
  Search,
  AlertTriangle,
  RotateCcw,
  RefreshCcw,
} from "lucide-react";

/* =========================================================
   Status Badge
   - 분석중/빌드중: 진행 단계/퍼센트 노출 X (배지만 표시)
========================================================= */
export const StatusBadge = ({ status }) => {
  const statusConfig = {
    analyzing: {
      label: "분석중",
      color: "bg-blue-50 text-blue-700 border-blue-200",
      icon: Loader2,
      animate: true,
    },
    progressing: {
      label: "빌드중",
      color: "bg-amber-50 text-amber-700 border-amber-200",
      icon: Clock,
      animate: true,
    },
    complete: {
      label: "완료",
      color: "bg-green-50 text-green-700 border-green-200",
      icon: CheckCircle,
      animate: false,
    },
    faild: {
      label: "실패",
      color: "bg-red-50 text-red-700 border-red-200",
      icon: AlertCircle,
      animate: false,
    },
    failed: {
      label: "실패",
      color: "bg-red-50 text-red-700 border-red-200",
      icon: AlertCircle,
      animate: false,
    },
  };

  const config = statusConfig[status] || statusConfig.failed;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}
      title={config.label}
    >
      <Icon className={`w-3.5 h-3.5 ${config.animate ? "animate-spin" : ""}`} />
      <span className="leading-none">{config.label}</span>
    </span>
  );
};

/* =========================================================
   Type Badge
========================================================= */
export const TypeBadge = ({ type, sharedBy }) => {
  const isShared = type === "build_typ_s001";
  return (
    <div className="flex items-center gap-1">
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${
          isShared
            ? "bg-purple-50 text-purple-700"
            : "bg-gray-100 text-gray-700"
        }`}
      >
        {isShared ? (
          <Share2 className="w-3.5 h-3.5" />
        ) : (
          <Database className="w-3.5 h-3.5" />
        )}
        {isShared ? "공유받음" : "직접구축"}
      </span>
      {isShared && sharedBy && (
        <span
          className="text-xs text-gray-500 truncate max-w-[120px]"
          title={sharedBy}
        >
          from {sharedBy}
        </span>
      )}
    </div>
  );
};

/* =========================================================
   Tooltip
   - 동적 offset은 style로 안전 처리
========================================================= */
export const Tooltip = ({
  children,
  content,
  position = "top",
  offset = 8,
  className = "",
}) => {
  const [open, setOpen] = useState(false);

  const baseStyle = {
    position: "absolute",
    zIndex: 50,
    pointerEvents: "none",
  };

  const posStyle = (() => {
    switch (position) {
      case "bottom":
        return {
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          marginTop: offset,
        };
      case "left":
        return {
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginRight: offset,
        };
      case "right":
        return {
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginLeft: offset,
        };
      case "top":
      default:
        return {
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          marginBottom: offset,
        };
    }
  })();

  const arrowClass = {
    top: "absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45",
    bottom:
      "absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45",
    left: "absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45",
    right:
      "absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45",
  }[position];

  return (
    <span className="relative inline-block">
      <span
        tabIndex={0}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex"
      >
        {children}
      </span>

      {open && (
        <div style={{ ...baseStyle, ...posStyle }} role="tooltip">
          <div
            className={`relative max-w-xs whitespace-normal break-words rounded-2xl bg-gray-900 text-white text-sm leading-relaxed p-3 shadow-xl ${className}`}
          >
            {typeof content === "string" ? <p>{content}</p> : content}
            <div className={arrowClass} />
          </div>
        </div>
      )}
    </span>
  );
};

/* =========================================================
   Create Database Modal
   - 진행 바/퍼센트 없음
========================================================= */
export const CreateDatabaseModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    database_name: "",
    database_description: "",
    file: null,
  });
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState("");

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    setFileError("");
    if (!file) return;
    if (file.type !== "application/pdf") {
      setFileError("PDF 파일만 업로드 가능합니다.");
      return;
    }
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      setFileError("파일 크기는 20MB 이하여야 합니다.");
      return;
    }
    setFormData((prev) => ({ ...prev, file }));
  };

  const handleSubmit = () => {
    if (
      !formData.database_name ||
      !formData.database_description ||
      !formData.file
    )
      return;
    onSubmit(formData);
  };

  const resetForm = () => {
    setFormData({ database_name: "", database_description: "", file: null });
    setFileError("");
  };

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                새 데이터베이스 생성
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                데이터베이스 이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.database_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    database_name: e.target.value.slice(0, 50),
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="예: 제품 매뉴얼"
                maxLength={50}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.database_name.length}/50
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                설명 <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                value={formData.database_description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    database_description: e.target.value.slice(0, 200),
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                rows={3}
                placeholder="데이터베이스에 대한 간단한 설명을 입력하세요"
                maxLength={200}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.database_description.length}/200
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PDF 파일 업로드 <span className="text-red-500">*</span>
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                  dragActive
                    ? "border-blue-500 bg-blue-50"
                    : fileError
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {formData.file ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <FileText className="w-12 h-12 text-red-500" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <span
                          className="text-sm text-gray-700 truncate"
                          title={formData.file.name}
                        >
                          {formData.file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData((prev) => ({ ...prev, file: null }));
                          setFileError("");
                        }}
                        className="text-gray-500 hover:text-red-500 ml-2 flex-shrink-0"
                        aria-label="파일 제거"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      크기: {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      PDF 파일을 드래그하거나 클릭하여 업로드
                    </p>
                    <p className="text-xs text-gray-500">최대 20MB</p>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="mt-3 inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                    >
                      파일 선택
                    </label>
                  </>
                )}
              </div>
              {fileError && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {fileError}
                </p>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium mb-1">안내사항</p>
                  <ul className="space-y-0.5">
                    <li>• PDF 파일은 AI가 분석하여 벡터 데이터로 변환됩니다</li>
                    <li>
                      • 변환 과정은 파일 크기에 따라 시간이 소요될 수 있습니다
                    </li>
                    <li>• 완료 후 스마트 에디터에서 활용 가능합니다</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !formData.database_name ||
                  !formData.database_description ||
                  !formData.file ||
                  isLoading
                }
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    생성 중...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    생성
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   Share Database Modal
========================================================= */
export const ShareDatabaseModal = ({
  isOpen,
  onClose,
  onShare,
  database,
  users = [],
  isLoading,
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const safeUsers = Array.isArray(users) ? users : [];
  const filteredUsers = safeUsers.filter((user) => {
    const name = (user.user_name || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    const q = searchTerm.toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const handleShare = () => {
    onShare(database.database_id, selectedUsers);
    setSelectedUsers([]);
    setSearchTerm("");
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Share2 className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                데이터베이스 공유
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4">
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={database?.database_name}
              >
                {database?.database_name}
              </p>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {database?.database_description}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <StatusBadge status={database?.database_status} />
                <span className="text-xs text-gray-500">
                  크기:{" "}
                  {database?.data_size
                    ? `${(database.data_size / (1024 * 1024)).toFixed(1)} MB`
                    : "0 MB"}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              공유할 사용자 선택
            </label>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="이름 또는 이메일로 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="space-y-1 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {searchTerm
                    ? "검색 결과가 없습니다"
                    : "공유 가능한 사용자가 없습니다"}
                </p>
              ) : (
                filteredUsers.map((user) => (
                  <label
                    key={user.user_id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers((prev) => [...prev, user.user_id]);
                        } else {
                          setSelectedUsers((prev) =>
                            prev.filter((id) => id !== user.user_id)
                          );
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.user_name}
                      </p>
                      <p className="text-xs text-gray-600 truncate">
                        {user.email}
                      </p>
                    </div>
                    <div
                      className="w-2 h-2 bg-green-400 rounded-full"
                      title="온라인"
                    />
                  </label>
                ))
              )}
            </div>

            {selectedUsers.length > 0 && (
              <p className="mt-2 text-xs text-purple-600">
                {selectedUsers.length}명 선택됨
              </p>
            )}
          </div>

          <div className="bg-amber-50 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-700">
                <p className="font-medium mb-1">공유 시 주의사항</p>
                <ul className="space-y-0.5">
                  <li>• 공유받은 사용자는 읽기 권한만 가지게 됩니다</li>
                  <li>• 공유를 취소하려면 데이터베이스를 삭제해야 합니다</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              취소
            </button>
            <button
              onClick={handleShare}
              disabled={selectedUsers.length === 0 || isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  공유 중...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  {selectedUsers.length}명에게 공유
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   Delete Confirmation Modal
========================================================= */
export const DeleteConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  database,
  isLoading,
}) => {
  if (!isOpen) return null;

  const isShared = database?.base_type === "build_typ_s001";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                데이터베이스 삭제
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">
              다음 데이터베이스를 삭제하시겠습니까?
            </p>
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={database?.database_name}
              >
                {database?.database_name}
              </p>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {database?.database_description}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <TypeBadge type={database?.base_type} />
                <StatusBadge status={database?.database_status} />
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-3 mb-6">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-700">
                <p className="font-medium mb-1">경고</p>
                {isShared ? (
                  <p>
                    공유받은 데이터베이스를 삭제하면 더 이상 접근할 수 없습니다.
                  </p>
                ) : (
                  <>
                    <p>이 작업은 되돌릴 수 없습니다.</p>
                    <p className="mt-1">
                      • 원본 파일과 모든 분석 데이터가 삭제됩니다
                    </p>
                    <p>• 다른 사용자에게 공유된 데이터베이스도 삭제됩니다</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              취소
            </button>
            <button
              onClick={() => onConfirm(database.database_id)}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  삭제 중...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  삭제
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   Database Card
   - 진행 단계/퍼센트 바는 완전히 제거
========================================================= */
export const DatabaseCard = ({
  database,
  onShare,
  onDelete,
  formatFileSize = (n) => {
    if (!n || n <= 0) return "0 MB";
    const mb = n / (1024 * 1024);
    if (mb < 1) return `${(n / 1024).toFixed(0)} KB`;
    return `${mb.toFixed(1)} MB`;
  },
  formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return iso;
    }
  },
}) => {
  const canShare =
    database.base_type === "build_typ_d001" &&
    database.database_status === "complete";

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md hover:-translate-y-[1px] transition-all duration-200 group">
      <div className="p-5">
        <div className="flex justify-between items-start mb-3 gap-3">
          <div className="flex-1 min-w-0">
            <h3
              className="text-lg font-semibold text-gray-900 mb-1 truncate"
              title={database.database_name}
            >
              {database.database_name}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-2">
              {database.database_description}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <TypeBadge
              type={database.base_type}
              sharedBy={database.shared_by}
            />
            <StatusBadge status={database.database_status} />
          </div>
        </div>

        {database.base_type === "build_typ_s001" &&
          database.shared_db_serial && (
            <div className="mb-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 flex items-center gap-1">
                <Database className="w-3.5 h-3.5" />
                원본 ID:&nbsp;
                <span className="font-medium">{database.shared_db_serial}</span>
              </p>
            </div>
          )}

        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
          <span title="데이터 크기">{formatFileSize(database.data_size)}</span>
          <span title="청크 개수">
            {typeof database.chunk_size === "number" ? database.chunk_size : 0}
            개 청크
          </span>
          <span title="생성일">{formatDate(database.created_at)}</span>
        </div>

        <div className="flex gap-2">
          {canShare && (
            <button
              onClick={() => onShare(database)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium group-hover:shadow-sm"
            >
              <Share2 className="w-4 h-4" />
              공유
            </button>
          )}
          <button
            onClick={() => onDelete(database)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium group-hover:shadow-sm"
          >
            <Trash2 className="w-4 h-4" />
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   Empty State
========================================================= */
export const EmptyState = ({
  searchTerm,
  filterType,
  filterStatus,
  onCreateClick,
}) => {
  const hasFilters =
    searchTerm || filterType !== "all" || filterStatus !== "all";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
      <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {hasFilters ? "검색 결과가 없습니다" : "아직 데이터베이스가 없습니다"}
      </h3>
      <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto">
        {hasFilters
          ? "다른 검색 조건을 시도해보세요"
          : "PDF 파일을 업로드하여 AI가 인지할 수 있는 지식 데이터베이스를 구축하세요"}
      </p>
      {!hasFilters && (
        <button
          onClick={onCreateClick}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-5 h-5" />첫 데이터베이스 생성
        </button>
      )}
    </div>
  );
};

/* =========================================================
   Loading Skeleton
========================================================= */
export const LoadingSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-lg border border-gray-200 p-5 animate-pulse"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-full" />
            </div>
            <div className="flex gap-2 ml-4">
              <div className="h-6 bg-gray-200 rounded w-16" />
              <div className="h-6 bg-gray-200 rounded w-12" />
            </div>
          </div>
          <div className="h-2 bg-gray-200 rounded w-full mb-3" />
          <div className="flex justify-between mb-3">
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
          <div className="flex gap-2">
            <div className="h-8 bg-gray-200 rounded flex-1" />
            <div className="h-8 bg-gray-200 rounded flex-1" />
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   Refresh Controls (요청사항)
   - 5분 주기 자동 onRefresh
   - 즉시 새로고침(소프트) + 페이지 새로고침(하드)
========================================================= */
export const RefreshControls = ({
  onRefresh, // 데이터를 다시 가져오는 함수 (부모에서 주입)
  intervalMs = 300000, // 기본 5분
  enabled = true,
  className = "",
  showHardReload = true,
}) => {
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      try {
        onRefresh && onRefresh();
      } catch (_) {}
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, onRefresh]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => onRefresh && onRefresh()}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
        title="데이터 다시 불러오기"
      >
        <RotateCcw className="w-4 h-4" />
        새로고침
      </button>
      {showHardReload && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          title="페이지 강제 새로고침"
        >
          <RefreshCcw className="w-4 h-4" />
          페이지 새로고침
        </button>
      )}
      <span className="ml-1 text-xs text-gray-400">(자동 5분)</span>
    </div>
  );
};
